import { Controller } from '@hotwired/stimulus';
import consumer from '../channels/consumer';

// Drives the negotiated takeover from the "template locked" screen. Instead of
// forcing the lock immediately, the waiting user asks the current editor to
// save and release first, then enters on fresh content. If the editor does not
// hand over within the timeout (dead / very slow tab), we fall back to the hard
// force (the form's native submit), where the server grace window still
// protects the previous editor's last save.
const GRANT_TIMEOUT_MS = 9000;

export default class extends Controller {
  static targets = ['button', 'label', 'spinner'];
  static values = { templateId: String, userId: Number, editorPath: String };

  connect() {
    if (!this.templateIdValue) return;

    this.requested = false;
    this.subscription = consumer.subscriptions.create(
      {
        channel: 'TemplateEditingChannel',
        template_id: this.templateIdValue,
        role: 'spectator',
      },
      { received: (data) => this.onMessage(data) },
    );
  }

  disconnect() {
    this.clearGrantTimeout();
    if (this.subscription) consumer.subscriptions.remove(this.subscription);
  }

  // Intercepts the "Forcer" submit: try the graceful handover first.
  request(event) {
    // No live channel: let the native submit (hard force) run.
    if (this.requested || !this.subscription) return;

    event.preventDefault();
    this.requested = true;
    this.form = event.target;

    this.showRequesting();
    this.subscription.perform('request_takeover');
    this.grantTimeout = setTimeout(() => this.fallbackForce(), GRANT_TIMEOUT_MS);
  }

  onMessage(data) {
    if (!data || data.action !== 'takeover_granted') return;
    if (data.requester_id !== this.userIdValue) return;

    this.clearGrantTimeout();
    window.location.href = this.editorPathValue;
  }

  // The editor never handed over in time: hard-force (grace protects its save).
  fallbackForce() {
    if (this.form) this.form.requestSubmit();
  }

  showRequesting() {
    if (this.hasButtonTarget) this.buttonTarget.disabled = true;
    if (this.hasSpinnerTarget) this.spinnerTarget.hidden = false;
    if (this.hasLabelTarget && this.labelTarget.dataset.requesting) {
      this.labelTarget.textContent = this.labelTarget.dataset.requesting;
    }
  }

  clearGrantTimeout() {
    if (this.grantTimeout) window.clearTimeout(this.grantTimeout);
    this.grantTimeout = null;
  }
}
